import type { StateCreator } from 'zustand';
import type {
  Risk,
  RiskMatrix,
  RiskMatrixConfig,
  RiskFilter,
  RiskStatistics,
} from '../../types/risk';
import { DEFAULT_RISK_MATRIX_CONFIG } from '../../types/risk';
import {
  enrichRisk,
  calculateRiskStatistics,
  filterRisks,
  sortRisks,
  generateRiskMatrix,
} from '../../utils/riskCalculator';

// ============================================================================
// RISK MANAGEMENT SLICE
// ============================================================================

export interface RiskSlice {
  // State
  risks: Risk[];
  riskMatrix: RiskMatrix | null;
  riskConfig: RiskMatrixConfig;
  riskFilter: RiskFilter;
  selectedRiskId: string | null;

  // Actions - CRUD operations
  addRisk: (risk: Partial<Risk>) => void;
  updateRisk: (id: string, updates: Partial<Risk>) => void;
  deleteRisk: (id: string) => void;
  duplicateRisk: (id: string) => void;

  // Actions - Risk management
  closeRisk: (id: string, comment?: string) => void;
  reopenRisk: (id: string) => void;
  assignRisk: (id: string, ownerId: string) => void;
  updateRiskStatus: (id: string, status: Risk['status']) => void;
  addRiskNote: (id: string, content: string, author?: string) => void;

  // Actions - Configuration
  updateRiskConfig: (config: Partial<RiskMatrixConfig>) => void;
  resetRiskConfig: () => void;

  // Actions - Filtering & Selection
  setRiskFilter: (filter: Partial<RiskFilter>) => void;
  clearRiskFilter: () => void;
  selectRisk: (id: string | null) => void;

  // Computed
  getFilteredRisks: () => Risk[];
  getRiskById: (id: string) => Risk | undefined;
  getRiskStatistics: () => RiskStatistics;
  calculateRiskMatrix: () => void;
}

export const createRiskSlice: StateCreator<any, [], [], RiskSlice> = (set, get) => {
  const canEdit = (): boolean => {
    const state = get();
    const role = state.currentProject?.currentUserRole;
    if (role) {
      const allowed = role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR';
      if (!allowed) {
        state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      }
      return allowed;
    }
    if (state.storageMode === 'backend') {
      state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      return false;
    }
    return true;
  };

  return ({
  // Initial state
  risks: [],
  riskMatrix: null,
  riskConfig: DEFAULT_RISK_MATRIX_CONFIG,
  riskFilter: {},
  selectedRiskId: null,

  // Add new risk
  addRisk: (riskData) => {
    if (!canEdit()) return;
    const { risks, riskConfig, currentProject } = get();

    if (!currentProject) {
      get().addNotification('error', 'Aucun projet actif');
      return;
    }

    const newRisk = enrichRisk(riskData, riskConfig);

    set({ risks: [...risks, newRisk] });

    get().addNotification('success', `Risque "${newRisk.title}" ajouté`);

    // Recalculate matrix
    get().calculateRiskMatrix();
  },

  // Update existing risk
  updateRisk: (id, updates) => {
    if (!canEdit()) return;
    const { risks, riskConfig } = get();
    const riskIndex = risks.findIndex((r: Risk) => r.id === id);

    if (riskIndex === -1) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    const oldRisk = risks[riskIndex];
    const updatedRisk = enrichRisk({ ...oldRisk, ...updates }, riskConfig);

    // Add history entry
    if (updates.status && updates.status !== oldRisk.status) {
      const historyEntry = {
        id: `history-${Date.now()}`,
        riskId: id,
        timestamp: new Date(),
        action: 'status_changed' as const,
        field: 'status',
        oldValue: oldRisk.status,
        newValue: updates.status,
      };

      updatedRisk.historyLog = [...(updatedRisk.historyLog || []), historyEntry];
    }

    const newRisks = [...risks];
    newRisks[riskIndex] = updatedRisk;

    set({ risks: newRisks });
    get().addNotification('success', 'Risque mis à jour');
    get().calculateRiskMatrix();
  },

  // Delete risk
  deleteRisk: (id) => {
    if (!canEdit()) return;
    const { risks } = get();
    const risk = risks.find((r: Risk) => r.id === id);

    if (!risk) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    set({ risks: risks.filter((r: Risk) => r.id !== id) });
    get().addNotification('success', `Risque "${risk.title}" supprimé`);
    get().calculateRiskMatrix();
  },

  // Duplicate risk
  duplicateRisk: (id) => {
    if (!canEdit()) return;
    const { risks, riskConfig } = get();
    const risk = risks.find((r: Risk) => r.id === id);

    if (!risk) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    const duplicatedRisk = enrichRisk(
      {
        ...risk,
        id: undefined,
        title: `${risk.title} (Copie)`,
        identifiedDate: new Date(),
        lastReviewDate: undefined,
        closedDate: undefined,
        status: 'identified',
        historyLog: [],
        notes: [],
      },
      riskConfig
    );

    set({ risks: [...risks, duplicatedRisk] });
    get().addNotification('success', 'Risque dupliqué');
    get().calculateRiskMatrix();
  },

  // Close risk
  closeRisk: (id, comment) => {
    if (!canEdit()) return;
    const { risks } = get();
    const risk = risks.find((r: Risk) => r.id === id);

    if (!risk) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    const historyEntry = {
      id: `history-${Date.now()}`,
      riskId: id,
      timestamp: new Date(),
      action: 'closed' as const,
      comment,
    };

    get().updateRisk(id, {
      status: 'closed',
      closedDate: new Date(),
      historyLog: [...(risk.historyLog || []), historyEntry],
    });

    get().addNotification('success', `Risque "${risk.title}" clôturé`);
  },

  // Reopen risk
  reopenRisk: (id) => {
    if (!canEdit()) return;
    const { risks } = get();
    const risk = risks.find((r: Risk) => r.id === id);

    if (!risk) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    get().updateRisk(id, {
      status: 'monitoring',
      closedDate: undefined,
    });

    get().addNotification('success', `Risque "${risk.title}" réouvert`);
  },

  // Assign risk to owner
  assignRisk: (id, ownerId) => {
    if (!canEdit()) return;
    const { risks } = get();
    const risk = risks.find((r: Risk) => r.id === id);

    if (!risk) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    const historyEntry = {
      id: `history-${Date.now()}`,
      riskId: id,
      timestamp: new Date(),
      action: 'reassigned' as const,
      field: 'owner',
      oldValue: risk.owner,
      newValue: ownerId,
    };

    get().updateRisk(id, {
      owner: ownerId,
      historyLog: [...(risk.historyLog || []), historyEntry],
    });

    get().addNotification('success', 'Responsable assigné');
  },

  // Update risk status
  updateRiskStatus: (id, status) => {
    if (!canEdit()) return;
    get().updateRisk(id, { status });
  },

  // Add note to risk
  addRiskNote: (id, content, author) => {
    if (!canEdit()) return;
    const { risks } = get();
    const risk = risks.find((r: Risk) => r.id === id);

    if (!risk) {
      get().addNotification('error', 'Risque introuvable');
      return;
    }

    const note = {
      id: `note-${Date.now()}`,
      riskId: id,
      author,
      content,
      createdAt: new Date(),
    };

    get().updateRisk(id, {
      notes: [...(risk.notes || []), note],
    });

    get().addNotification('success', 'Note ajoutée');
  },

  // Update configuration
  updateRiskConfig: (config) => {
    if (!canEdit()) return;
    set((state: any) => ({
      riskConfig: {
        ...state.riskConfig,
        ...config,
      },
    }));

    // Recalculate all risks with new config
    const { risks, riskConfig } = get();
    const recalculatedRisks = risks.map((risk: Risk) => enrichRisk(risk, riskConfig));

    set({ risks: recalculatedRisks });
    get().calculateRiskMatrix();
  },

  // Reset configuration
  resetRiskConfig: () => {
    if (!canEdit()) return;
    set({ riskConfig: DEFAULT_RISK_MATRIX_CONFIG });
    get().addNotification('info', 'Configuration réinitialisée');
  },

  // Set filter
  setRiskFilter: (filter) => {
    set((state: any) => ({
      riskFilter: {
        ...state.riskFilter,
        ...filter,
      },
    }));
  },

  // Clear filter
  clearRiskFilter: () => {
    set({ riskFilter: {} });
  },

  // Select risk
  selectRisk: (id) => {
    set({ selectedRiskId: id });
  },

  // Get filtered risks
  getFilteredRisks: () => {
    const { risks, riskFilter, riskConfig } = get();
    let filtered = filterRisks(risks, riskFilter);

    // Apply config filters
    if (!riskConfig.showClosedRisks) {
      filtered = filtered.filter((r) => r.status !== 'closed');
    }

    // Apply sorting
    filtered = sortRisks(filtered, riskConfig.sortBy);

    return filtered;
  },

  // Get risk by ID
  getRiskById: (id) => {
    const { risks } = get();
    return risks.find((r: Risk) => r.id === id);
  },

  // Get statistics
  getRiskStatistics: () => {
    const { risks, riskConfig } = get();
    return calculateRiskStatistics(risks, riskConfig);
  },

  // Calculate risk matrix
  calculateRiskMatrix: () => {
    const { risks, currentProject } = get();

    if (!currentProject) {
      return;
    }

    const matrix = generateRiskMatrix(currentProject.id, risks);
    set({ riskMatrix: matrix });
  },
  });
};
